import PropTypes from 'prop-types';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import ButtonV2 from 'components/ButtonV2';
import PaginationControls from 'components/PaginationControls';
import TicketBreakdown from 'components/TicketBreakdown';
import {
  TransactionCard,
  TransactionHeader,
  TransactionTitle,
  TabsContainer,
  Tab,
  TransactionList,
  TransactionItem,
  TransactionItemHeader,
  TransactionItemLeft,
  StatusBadge,
  DateBadge,
  EventName,
  EventSummary,
  TransactionRightSection,
  TransactionAmount,
  ExpandIcon,
  TransactionDetails,
  DetailGrid,
  DetailColumn,
  DetailLabel,
  DetailValue,
  DetailActions,
} from 'pages/track/Customers/CustomerDetail/StyledComponents';
import { formatTimestamp } from 'shared/formatters';

// Helper function to get ticket/pass text based on platform
const getTicketText = (count = 1) => {
  const isTicketHoss = process.env.REACT_APP_PLATFORM === 'tickethoss';
  if (isTicketHoss) {
    return count === 1 ? 'ticket' : 'tickets';
  }
  return count === 1 ? 'pass' : 'passes';
};

const TransactionListSection = ({
  title,
  transactions,
  transactionData,
  hasTransactions,
  expandedItems,
  toggleExpand,
  currentPage,
  totalPages,
  totalCount,
  handlePageChange,
  getPageNumbers,
  yearCountsData,
  activeTab,
  setActiveTab,
  setCurrentPage,
  showPurchaser,
  routePrefix,
  userId,
  isAdmin,
  style,
}) => {
  const history = useHistory();
  const location = useLocation();

  return (
    <TransactionCard style={style}>
      <TransactionHeader>
        <TransactionTitle>{title}</TransactionTitle>
      </TransactionHeader>

      {hasTransactions && (
        <TabsContainer>
          <Tab
            active={activeTab === 'All'}
            onClick={() => {
              setActiveTab('All');
              setCurrentPage(1);
              const searchParams = new URLSearchParams(location.search);
              searchParams.delete('year');
              searchParams.delete('thirdPartyYear');
              searchParams.set('page', '1');
              history.push({
                pathname: location.pathname,
                search: searchParams.toString(),
              });
            }}
          >
            All ({totalCount})
          </Tab>

          {yearCountsData
            .sort((a, b) => {
              const yearA = String(a.year);
              const yearB = String(b.year);
              return yearB.localeCompare(yearA);
            })
            .map(({ year, count }) => (
              <Tab
                key={year}
                active={String(activeTab) === String(year)}
                onClick={() => {
                  setActiveTab(String(year));
                  setCurrentPage(1);
                  const searchParams = new URLSearchParams(location.search);
                  if (showPurchaser) {
                    searchParams.set('thirdPartyYear', String(year));
                    searchParams.set('thirdPartyPage', '1');
                  } else {
                    searchParams.set('year', String(year));
                    searchParams.set('page', '1');
                  }
                  history.push({
                    pathname: location.pathname,
                    search: searchParams.toString(),
                  });
                }}
              >
                {year} ({count})
              </Tab>
            ))}
        </TabsContainer>
      )}

      <TransactionList>
        {hasTransactions && transactions.length > 0 ? (
          transactions.map((transaction) => (
            <TransactionItem key={transaction.id}>
              <TransactionItemHeader
                onClick={() => toggleExpand(transaction.id)}
              >
                <TransactionItemLeft>
                  <DateBadge>
                    <div className="date-day">{transaction.day}</div>
                    {transaction.month && (
                      <div className="date-month">{transaction.month}</div>
                    )}
                  </DateBadge>
                  <div>
                    <EventName>{transaction.eventName}</EventName>
                    <EventSummary>
                      {showPurchaser && transaction.purchaser && (
                        <span>Purchased by: {transaction.purchaser}</span>
                      )}
                      <span>{transaction.trackName}</span>
                      <span>
                        {`${transaction.ticketCount} ${getTicketText(
                          transaction.ticketCount
                        )}`}
                      </span>
                    </EventSummary>
                  </div>
                </TransactionItemLeft>
                <TransactionRightSection>
                  {transaction.eventStatus &&
                    (transaction.eventStatus === 'cancelled' ||
                      transaction.eventStatus === 'postponed') && (
                      <StatusBadge status={transaction.eventStatus}>
                        {transaction.eventStatus === 'cancelled'
                          ? 'Cancelled'
                          : 'Postponed'}
                      </StatusBadge>
                    )}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                    }}
                  >
                    <TransactionAmount>
                      $
                      {transaction.profit.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </TransactionAmount>
                    <ExpandIcon
                      expanded={expandedItems[transaction.id]}
                      viewBox="0 0 16 16"
                      fill="currentColor"
                    >
                      <path d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" />
                    </ExpandIcon>
                  </div>
                </TransactionRightSection>
              </TransactionItemHeader>

              {expandedItems[transaction.id] && (
                <TransactionDetails>
                  <DetailGrid>
                    <DetailColumn>
                      <div>
                        <DetailLabel>
                          {getTicketText(2).toUpperCase()} PURCHASED
                        </DetailLabel>
                        <TicketBreakdown
                          tickets={transaction.tickets}
                          emptyText={
                            transaction.ticketInfo ||
                            `No ${getTicketText(2)} details`
                          }
                        />
                      </div>
                    </DetailColumn>
                    <DetailColumn alignRight>
                      <div>
                        <DetailLabel>PURCHASE DATE</DetailLabel>
                        <DetailValue>
                          {formatTimestamp(
                            Number(transaction.purchase_date),
                            'MMM D, YYYY h:mm A',
                            true
                          )}
                        </DetailValue>
                      </div>
                      <div>
                        <DetailLabel>FEE</DetailLabel>
                        <DetailValue>
                          $
                          {transaction.fee.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </DetailValue>
                      </div>
                      <div>
                        <DetailLabel>TOTAL</DetailLabel>
                        <DetailValue>
                          $
                          {transaction.total.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </DetailValue>
                      </div>
                      <div>
                        <DetailLabel>PROFIT</DetailLabel>
                        <DetailValue>
                          $
                          {transaction.profit.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </DetailValue>
                      </div>
                    </DetailColumn>
                  </DetailGrid>

                  <DetailActions>
                    {(() => {
                      const rawTransaction = transactionData.find(
                        (t) => t.id === transaction.id
                      );

                      const areAllTicketsRefunded = () => {
                        if (!rawTransaction?.users) return false;
                        let totalTickets = 0;
                        let refundedTickets = 0;
                        rawTransaction.users.forEach((user) => {
                          if (user.tickets) {
                            user.tickets.forEach((ticket) => {
                              totalTickets++;
                              if (ticket.refunded) {
                                refundedTickets++;
                              }
                            });
                          }
                        });
                        return (
                          totalTickets > 0 && totalTickets === refundedTickets
                        );
                      };

                      const areAnyTicketsCredited = () => {
                        if (!rawTransaction?.users) return false;
                        return rawTransaction.users.some((user) =>
                          user.tickets?.some((ticket) => ticket.credited)
                        );
                      };

                      const shouldShowRefund = isAdmin;
                      const shouldShowCredit = isAdmin;

                      return (
                        <>
                          {shouldShowRefund && (
                            <ButtonV2
                              variant="pill"
                              onClick={() => {
                                const returnPath = `${routePrefix}/users/transactions/${userId}`;
                                history.replace(
                                  `${routePrefix}/transactions/${
                                    transaction.id
                                  }/refund?customerId=${userId}&returnTo=${encodeURIComponent(
                                    returnPath
                                  )}`
                                );
                              }}
                              disabled={
                                areAllTicketsRefunded() &&
                                !areAnyTicketsCredited()
                              }
                              title={
                                areAllTicketsRefunded() &&
                                !areAnyTicketsCredited()
                                  ? 'All tickets have already been refunded'
                                  : 'Process refund for this transaction'
                              }
                              icon={
                                <svg
                                  width="16"
                                  height="16"
                                  fill="currentColor"
                                  viewBox="0 0 16 16"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M1 11.5a.5.5 0 0 0 .5.5h11.793l-3.147 3.146a.5.5 0 0 0 .708.708l4-4a.5.5 0 0 0 0-.708l-4-4a.5.5 0 1 0-.708.708L13.293 11H1.5a.5.5 0 0 0-.5.5zm14-7a.5.5 0 0 1-.5.5H2.707l3.147 3.146a.5.5 0 1 1-.708.708l-4-4a.5.5 0 0 1 0-.708l4-4a.5.5 0 1 1 .708.708L2.707 4H14.5a.5.5 0 0 1 .5.5z"
                                  />
                                </svg>
                              }
                            >
                              Refund
                            </ButtonV2>
                          )}
                          {shouldShowCredit && (
                            <ButtonV2
                              variant="pill"
                              onClick={() => {
                                const returnPath = `${routePrefix}/users/transactions/${userId}`;
                                history.replace(
                                  `${routePrefix}/transactions/${
                                    transaction.id
                                  }/credit?customerId=${userId}&returnTo=${encodeURIComponent(
                                    returnPath
                                  )}`
                                );
                              }}
                              disabled={areAllTicketsRefunded()}
                              title={
                                areAllTicketsRefunded()
                                  ? 'Cannot issue credit - all tickets have been refunded'
                                  : 'Issue credit for this transaction'
                              }
                              icon={
                                <svg
                                  width="16"
                                  height="16"
                                  fill="currentColor"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z" />
                                </svg>
                              }
                            >
                              Credit
                            </ButtonV2>
                          )}
                          <ButtonV2
                            variant="pill"
                            active
                            onClick={() => {
                              history.push(
                                `${routePrefix}/transactions/detail/${transaction.id}`
                              );
                            }}
                            icon={
                              <svg
                                width="16"
                                height="16"
                                fill="currentColor"
                                viewBox="0 0 16 16"
                              >
                                <path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z" />
                                <path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8zm8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z" />
                              </svg>
                            }
                          >
                            View
                          </ButtonV2>
                        </>
                      );
                    })()}
                  </DetailActions>
                </TransactionDetails>
              )}
            </TransactionItem>
          ))
        ) : (
          <div
            style={{
              padding: '48px 24px',
              textAlign: 'center',
              color: '#9CA3AF',
              fontSize: '14px',
              lineHeight: '1.5',
            }}
          >
            <svg
              width="48"
              height="48"
              fill="currentColor"
              viewBox="0 0 16 16"
              style={{
                marginBottom: '16px',
                opacity: '0.5',
                width: '48px',
                height: '48px',
              }}
            >
              <path d="M0 2.5A.5.5 0 0 1 .5 2H2a.5.5 0 0 1 .485.379L2.89 4H14.5a.5.5 0 0 1 .485.621l-1.5 6A.5.5 0 0 1 13 11H4a.5.5 0 0 1-.485-.379L1.61 3H.5a.5.5 0 0 1-.5-.5M3.14 5l1.25 5h8.22l1.25-5zM5 13a1 1 0 1 0 0 2 1 1 0 0 0 0-2m-2 1a2 2 0 1 1 4 0 2 2 0 0 1-4 0m9-1a1 1 0 1 0 0 2 1 1 0 0 0 0-2m-2 1a2 2 0 1 1 4 0 2 2 0 0 1-4 0" />
            </svg>
            <div style={{ fontWeight: '500', marginBottom: '8px' }}>
              No transactions found
            </div>
            <div style={{ color: '#6B7280', fontSize: '13px' }}>
              {showPurchaser
                ? 'No one has purchased tickets for this user yet.'
                : "This user hasn't made any purchases yet."}
            </div>
          </div>
        )}
      </TransactionList>

      {/* Pagination */}
      {hasTransactions && totalPages > 1 && (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          getPageNumbers={getPageNumbers}
        />
      )}
    </TransactionCard>
  );
};

TransactionListSection.propTypes = {
  title: PropTypes.string.isRequired,
  transactions: PropTypes.array.isRequired,
  transactionData: PropTypes.array.isRequired,
  hasTransactions: PropTypes.bool.isRequired,
  expandedItems: PropTypes.object.isRequired,
  toggleExpand: PropTypes.func.isRequired,
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  handlePageChange: PropTypes.func.isRequired,
  getPageNumbers: PropTypes.func.isRequired,
  yearCountsData: PropTypes.array.isRequired,
  activeTab: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  setActiveTab: PropTypes.func.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  showPurchaser: PropTypes.bool,
  routePrefix: PropTypes.string.isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isAdmin: PropTypes.bool.isRequired,
  style: PropTypes.object,
};

TransactionListSection.defaultProps = {
  showPurchaser: false,
  style: {},
};

export default TransactionListSection;
